import { useCallback, useMemo, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService from '@services/BrandingsService';
import { runWithConcurrency } from '@shared/utils/mapTemplateDrift';

import { useDrift } from '../DriftContext';
import { useEditorMode } from '../EditorModeContext';
import {
  blinkSpotInUnreal,
  teleportToSpotInUnreal,
} from '../brandingSpotsUnreal';
import { useEditorStyles } from '../styles';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const sheetStyles = {
  scrim: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(3px)',
    zIndex: 1290,
    pointerEvents: 'auto',
  },
  sheet: {
    position: 'fixed',
    right: '22px',
    bottom: '78px',
    width: '412px',
    maxWidth: 'calc(100vw - 44px)',
    maxHeight: '72vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 1291,
    pointerEvents: 'auto',
  },
  head: {
    padding: '16px 18px 14px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#fff',
  },
  sub: {
    fontSize: '12.5px',
    color: 'rgba(255, 255, 255, 0.7)',
    marginTop: '5px',
    lineHeight: 1.5,
  },
  body: {
    overflowY: 'auto',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  subhead: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '11px 8px 7px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  subheadAmber: { color: '#f0b357' },
  subheadRed: { color: '#ef8b86' },
  subheadCount: {
    marginLeft: 'auto',
    fontFamily: MONO,
    color: 'rgba(255, 255, 255, 0.4)',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '11px',
    padding: '9px 10px',
    borderRadius: '10px',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
  },
  rowIconMissing: {
    width: 30,
    height: 30,
    borderRadius: '8px',
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    backgroundColor: 'rgba(245, 166, 35, 0.16)',
    color: '#f0b357',
  },
  rowIconOrphan: {
    width: 30,
    height: 30,
    borderRadius: '8px',
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    backgroundColor: 'rgba(255, 77, 77, 0.16)',
    color: '#ef8b86',
  },
  rowMain: { flex: 1, minWidth: 0 },
  rowName: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#fff',
    minWidth: 0,
  },
  rowNameText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowSub: {
    fontFamily: MONO,
    fontSize: '10.5px',
    color: 'rgba(255, 255, 255, 0.4)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowActions: { display: 'flex', gap: '3px', flexShrink: 0 },
  actionBtn: {
    width: 30,
    height: 30,
    color: 'rgba(255, 255, 255, 0.7)',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    borderRadius: '7px',
    '&:hover': {
      color: '#fff',
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
    '&.Mui-disabled': { opacity: 0.35 },
  },
  newBadge: {
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    padding: '1px 5px',
    borderRadius: '5px',
    backgroundColor: 'rgba(124, 108, 240, 0.22)',
    color: '#c8bdff',
    flexShrink: 0,
  },
  foot: {
    display: 'flex',
    gap: '9px',
    padding: '13px 16px',
    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
  },
  footBtnGhost: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    flex: 1,
    borderRadius: '11px',
    padding: '10px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    color: '#fff',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
  },
  footBtnAmber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    flex: 1,
    borderRadius: '11px',
    padding: '10px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    backgroundColor: '#f5a623',
    color: '#231905',
    border: 'none',
    '&:hover': { filter: 'brightness(1.05)' },
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
  empty: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: '12.5px',
    padding: '18px 12px',
    textAlign: 'center',
    fontStyle: 'italic',
  },
};

interface DriftSyncSheetProps {
  open: boolean;
  onClose: () => void;
}

const DriftSyncSheet = ({ open, onClose }: DriftSyncSheetProps) => {
  const { t: translate } = useTranslation();
  const editorStyles = useEditorStyles();
  const { spotDrift, rollup, refreshSpots, refetchTemplates } = useDrift();
  const { currentBrandingId } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const { setAutosaveStatus } = useEditorMode();

  const [busy, setBusy] = useState(false);

  const missingRows = useMemo(
    () => spotDrift.rows.filter((row) => row.status === 'missing'),
    [spotDrift],
  );
  const orphanRows = useMemo(
    () => spotDrift.rows.filter((row) => row.status === 'orphaned'),
    [spotDrift],
  );

  const createPlaceholder = useCallback(
    (ueIdentifier: string, type: string) => {
      if (!currentBrandingId) {
        return Promise.resolve();
      }
      return BrandingsService.createBrandingSpot(currentBrandingId, {
        isActive: false,
        type,
        name: ueIdentifier,
        ueIdentifier,
        data: { location: '', url: '' },
      });
    },
    [currentBrandingId],
  );

  const handleAddOne = useCallback(
    async (ueIdentifier: string, type: string) => {
      if (!currentBrandingId) {
        return;
      }
      setBusy(true);
      setAutosaveStatus('saving');
      try {
        await createPlaceholder(ueIdentifier, type);
        await refreshSpots();
        await refetchTemplates();
        setAutosaveStatus('saved');
      } catch (addError) {
        console.error(
          `Failed to add placeholder: ${(addError as Error).message}`,
          { brandingId: currentBrandingId, ueIdentifier },
        );
        setAutosaveStatus('error');
      } finally {
        setBusy(false);
      }
    },
    [
      currentBrandingId,
      createPlaceholder,
      refreshSpots,
      refetchTemplates,
      setAutosaveStatus,
    ],
  );

  const handleAddAll = useCallback(async () => {
    if (!currentBrandingId || missingRows.length === 0) {
      return;
    }
    setBusy(true);
    setAutosaveStatus('saving');
    let failures = 0;
    await runWithConcurrency(missingRows, 6, async (row) => {
      try {
        await createPlaceholder(row.ueIdentifier, row.type);
      } catch (addError) {
        failures += 1;
        console.error(
          `Failed to add placeholder: ${(addError as Error).message}`,
          { brandingId: currentBrandingId, ueIdentifier: row.ueIdentifier },
        );
      }
    });
    await refreshSpots();
    await refetchTemplates();
    setAutosaveStatus(failures > 0 ? 'error' : 'saved');
    setBusy(false);
  }, [
    currentBrandingId,
    missingRows,
    createPlaceholder,
    refreshSpots,
    refetchTemplates,
    setAutosaveStatus,
  ]);

  const handleRemoveOne = useCallback(
    async (brandingSpotId?: string) => {
      if (!currentBrandingId || !brandingSpotId) {
        return;
      }
      setBusy(true);
      setAutosaveStatus('saving');
      try {
        await BrandingsService.deleteBrandingSpot(
          currentBrandingId,
          brandingSpotId,
        );
        await refreshSpots();
        await refetchTemplates();
        setAutosaveStatus('saved');
      } catch (removeError) {
        console.error(
          `Failed to remove spot: ${(removeError as Error).message}`,
          { brandingId: currentBrandingId, brandingSpotId },
        );
        setAutosaveStatus('error');
      } finally {
        setBusy(false);
      }
    },
    [currentBrandingId, refreshSpots, refetchTemplates, setAutosaveStatus],
  );

  const handleTeleport = useCallback(
    (ueIdentifier: string) => {
      teleportToSpotInUnreal(sendMessageToUnreal, ueIdentifier);
    },
    [sendMessageToUnreal],
  );

  const handleHighlight = useCallback(
    (ueIdentifier: string) => {
      blinkSpotInUnreal(sendMessageToUnreal, ueIdentifier);
    },
    [sendMessageToUnreal],
  );

  if (!open) {
    return null;
  }

  return (
    <>
      <Box
        sx={sheetStyles.scrim}
        onClick={onClose}
        data-testid="drift-sync-sheet-scrim"
      />
      <Box sx={[editorStyles.glassPanel, sheetStyles.sheet]}>
        <Box sx={sheetStyles.head}>
          <Typography sx={sheetStyles.title}>
            {translate('editorMode.syncTitle')}
          </Typography>
          <Typography sx={sheetStyles.sub}>
            {translate('editorMode.syncSubtitle', {
              missing: rollup.missingSpots,
              orphaned: rollup.orphanedSpots,
            })}
          </Typography>
        </Box>

        <Box sx={sheetStyles.body}>
          {missingRows.length === 0 && orphanRows.length === 0 ? (
            <Typography sx={sheetStyles.empty}>
              {translate('editorMode.syncNoDrift')}
            </Typography>
          ) : null}

          {missingRows.length > 0 ? (
            <>
              <Box sx={[sheetStyles.subhead, sheetStyles.subheadAmber]}>
                <AddIcon sx={{ fontSize: 13 }} />
                <span>{translate('editorMode.syncMissingHead')}</span>
                <Box component="span" sx={sheetStyles.subheadCount}>
                  {missingRows.length}
                </Box>
              </Box>
              {missingRows.map((row) => (
                <Box key={`missing:${row.ueIdentifier}`} sx={sheetStyles.row}>
                  <Box sx={sheetStyles.rowIconMissing}>
                    <ImageOutlinedIcon sx={{ fontSize: 15 }} />
                  </Box>
                  <Box sx={sheetStyles.rowMain}>
                    <Box sx={sheetStyles.rowName}>
                      <Box component="span" sx={sheetStyles.rowNameText}>
                        {row.ueIdentifier}
                      </Box>
                      {row.isNew ? (
                        <Box component="span" sx={sheetStyles.newBadge}>
                          {translate('editorMode.driftNew')}
                        </Box>
                      ) : null}
                    </Box>
                    <Typography sx={sheetStyles.rowSub}>
                      {row.regionName || row.type}
                    </Typography>
                  </Box>
                  <Box sx={sheetStyles.rowActions}>
                    <Tooltip title={translate('editorMode.highlightSpot')}>
                      <IconButton
                        size="small"
                        sx={sheetStyles.actionBtn}
                        onClick={() => handleHighlight(row.ueIdentifier)}
                        data-testid={`drift-sync-missing-${row.ueIdentifier}-highlight-btn`}
                      >
                        <CenterFocusStrongOutlinedIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={translate('editorMode.teleportToSpot')}>
                      <IconButton
                        size="small"
                        sx={sheetStyles.actionBtn}
                        onClick={() => handleTeleport(row.ueIdentifier)}
                        data-testid={`drift-sync-missing-${row.ueIdentifier}-teleport-btn`}
                      >
                        <MyLocationOutlinedIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={translate('editorMode.driftAddToConfig')}>
                      <span>
                        <IconButton
                          size="small"
                          sx={sheetStyles.actionBtn}
                          disabled={busy}
                          onClick={() =>
                            handleAddOne(row.ueIdentifier, row.type)
                          }
                          data-testid={`drift-sync-missing-${row.ueIdentifier}-add-btn`}
                        >
                          <AddIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </>
          ) : null}

          {orphanRows.length > 0 ? (
            <>
              <Box sx={[sheetStyles.subhead, sheetStyles.subheadRed]}>
                <ReportProblemOutlinedIcon sx={{ fontSize: 13 }} />
                <span>{translate('editorMode.syncOrphanHead')}</span>
                <Box component="span" sx={sheetStyles.subheadCount}>
                  {orphanRows.length}
                </Box>
              </Box>
              {orphanRows.map((row) => (
                <Box key={`orphan:${row.ueIdentifier}`} sx={sheetStyles.row}>
                  <Box sx={sheetStyles.rowIconOrphan}>
                    <ReportProblemOutlinedIcon sx={{ fontSize: 15 }} />
                  </Box>
                  <Box sx={sheetStyles.rowMain}>
                    <Box sx={sheetStyles.rowName}>
                      <Box component="span" sx={sheetStyles.rowNameText}>
                        {row.brandingSpot?.name || row.ueIdentifier}
                      </Box>
                    </Box>
                    <Typography sx={sheetStyles.rowSub}>
                      {translate('editorMode.driftRemoved')}
                    </Typography>
                  </Box>
                  <Box sx={sheetStyles.rowActions}>
                    <Tooltip
                      title={translate('editorMode.driftRemoveFromConfig')}
                    >
                      <span>
                        <IconButton
                          size="small"
                          sx={sheetStyles.actionBtn}
                          disabled={busy || !row.brandingSpot?.brandingSpotId}
                          onClick={() =>
                            handleRemoveOne(row.brandingSpot?.brandingSpotId)
                          }
                          data-testid={`drift-sync-orphaned-${row.brandingSpot?.brandingSpotId}-remove-btn`}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </>
          ) : null}
        </Box>

        <Box sx={sheetStyles.foot}>
          <Box
            component="button"
            type="button"
            sx={sheetStyles.footBtnGhost}
            onClick={onClose}
            data-testid="drift-sync-later-btn"
          >
            {translate('editorMode.syncLater')}
          </Box>
          <Box
            component="button"
            type="button"
            sx={sheetStyles.footBtnAmber}
            onClick={handleAddAll}
            disabled={busy || missingRows.length === 0}
            data-testid="drift-sync-add-all-btn"
          >
            <AddIcon sx={{ fontSize: 16 }} />
            {translate('editorMode.syncAddAll', {
              count: missingRows.length,
            })}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DriftSyncSheet;
